import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { push } from '@lagunovsky/redux-react-router'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import moment from 'moment'
import { groupBy, isEmpty, isNil, range, toLower, uniqBy } from 'lodash'
import { ButtonGroup, Grid } from '@mui/material'
import ProgressCard from '../../../components/card/ProgressCard'
import Select from '../../../components/forms/Select'
import SimpleCheckbox from '../../../components/forms/SimpleCheckbox'
import { ButtonMUI } from '../../../components/styled/Buttons'
import LogAction from '../../../log/actions/LogAction'
import ToastrAction from '../../../toastr/actions/ToastrAction'
import ProductionUnitAction from '../../actions/ProductionUnitAction'
import ProductionUnitFilterPanel from '../../components/dashboard/ProductionUnitFilterPanel'
import useProductionUnitsData from '../../utils/useProductionUnitsData'
import useProductionUnitsFilter from '../../utils/useProductionUnitsFilter'
import useAbortController from '../../../utils/customHook/useAbortController'
import useActions from '../../../utils/customHook/useActions'
import useLocalStorage from '../../../utils/customHook/useLocalStorage'
import useTitle from '../../../utils/customHook/useTitle'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { H_PRODUCTION_VOLUMES } from '../../../account/constants/AccessRulesConstants'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from '../../../station/constants/StationConstants'
import { PRODUCTION_UNIT_LINK_TYPES } from '../../constants/ProductionUnitConstants'
import { getDate, getMonthYear, getWeekYear, getYearDate } from '../../../utils/DateUtil'
import { getI18nTitleDataLength, geti18n } from '../../../utils/StringUtil'
import { getManyTableData } from '../../../utils/ExportDataUtil'
import { getProductionUnitLabel, getVolumesRows, toVolumesExportRows } from '../../utils/ProductionUnitVolumesUtils'
import ProductionUnitVolumesTable from './ProductionUnitVolumesTable'

const OCCURENCE_TYPE = {
    DAY: { value: 'DAY', defaultValue: 7, nbValue: 30 },
    WEEK: { value: 'WEEK', defaultValue: 8, nbValue: 52 },
    MONTH: { value: 'MONTH', defaultValue: 12, nbValue: 24 },
    YEAR: { value: 'YEAR', defaultValue: 5, nbValue: 20 },
}

const BUTTONS_OCCURENCE = [
    { value: 'DAY', i18nKey: 'day', borderWidth: '2px 0px 2px 2px' },
    { value: 'WEEK', i18nKey: 'week', borderWidth: 2 },
    { value: 'MONTH', i18nKey: 'month', borderWidth: '2px 1px' },
    { value: 'YEAR', i18nKey: 'year', borderWidth: '2px 2px 2px 1px' },
]

const IN_SITE_TYPES = [STATION_TYPE_CONSTANT.piezometry, STATION_TYPE_CONSTANT.hydrometry]
const stationType = STATION_TYPE_NAME.productionUnit

const VOLUMES_FILTERS_STORAGE = 'PRODUCTION_UNITS_VOLUMES_FILTERS'
const DEFAULT_VOLUMES_FILTERS = {
    occurence: OCCURENCE_TYPE.DAY.value,
    nbOccurence: OCCURENCE_TYPE.DAY.defaultValue,
    onlyWithData: false,
}

// Les stations d'une UP sont déjà dans le store (associations chargées par useProductionUnitsData) :
// l'appel volumes ne rapatrie que les mesures.
const toVolumeStation = association => ({
    siteType: association.stationLinkedType,
    siteCode: association.stationLinkedId,
    code: association.stationLinkedCode,
    name: association.stationLinkedName,
})

const ProductionUnitsVolumesPanel = () => {
    const dispatch = useDispatch()
    const [measures, setMeasures] = useState([])
    const [volumesLoaded, setVolumesLoaded] = useState(false)
    const [expandedUnits, setExpandedUnits] = useLocalStorage('PRODUCTION_UNITS_VOLUMES_EXPANDED', {})

    // Filtres appliqués (persistés), et leur brouillon : comme le reste du panneau, ils ne prennent
    // effet qu'au clic sur Rechercher.
    const [appliedFilters, setAppliedFilters] = useLocalStorage(
        VOLUMES_FILTERS_STORAGE,
        DEFAULT_VOLUMES_FILTERS,
        stored => ({ ...DEFAULT_VOLUMES_FILTERS, ...stored }),
    )
    const occurence = OCCURENCE_TYPE[appliedFilters.occurence] || OCCURENCE_TYPE.DAY
    const { nbOccurence, onlyWithData } = appliedFilters
    const occurenceFilter = useMemo(() => ({ occurence: occurence.value, nbOccurence }), [nbOccurence, occurence.value])

    const [tmpOccurence, setTmpOccurence] = useState(occurence)
    const [tmpNbOccurence, setTmpNbOccurence] = useState(nbOccurence)
    const [tmpOnlyWithData, setTmpOnlyWithData] = useState(onlyWithData)

    const onSearch = useCallback(() => setAppliedFilters({
        occurence: tmpOccurence.value,
        nbOccurence: tmpNbOccurence,
        onlyWithData: tmpOnlyWithData,
    }), [setAppliedFilters, tmpNbOccurence, tmpOccurence, tmpOnlyWithData])

    const { productionUnitsAssociations } = useSelector(store => ({
        productionUnitsAssociations: store.ProductionUnitReducer.productionUnitsAssociations,
    }), shallowEqual)

    const { controllerRef, initController } = useAbortController(true)

    const { data, filterPanelProps, resetFilters, indicatorsLoaded, indicatorsProgress } = useProductionUnitsFilter()

    // incrémenté à la réinitialisation pour remonter le panneau de filtres avec les valeurs par défaut
    const [resetKey, setResetKey] = useState(0)
    const onReset = useCallback(() => {
        resetFilters()
        setAppliedFilters(DEFAULT_VOLUMES_FILTERS)
        setTmpOccurence(OCCURENCE_TYPE[DEFAULT_VOLUMES_FILTERS.occurence])
        setTmpNbOccurence(DEFAULT_VOLUMES_FILTERS.nbOccurence)
        setTmpOnlyWithData(DEFAULT_VOLUMES_FILTERS.onlyWithData)
        setResetKey(prev => prev + 1)
    }, [resetFilters, setAppliedFilters])

    const filteredUnits = data.stations
    const filteredUnitIds = useMemo(() => filteredUnits.map(({ id }) => id), [filteredUnits])

    const formatDate = useCallback(date => {
        switch (occurence.value) {
            case 'WEEK':
                return getWeekYear(date)
            case 'MONTH':
                return getMonthYear(date)
            case 'YEAR':
                // getYearDate et non getYear : la période sert de clé de colonne, il faut une chaîne.
                return getYearDate(date)
            default:
                return getDate(date)
        }
    }, [occurence])

    useEffect(() => {
        if (!indicatorsLoaded) {
            return
        }
        if (isEmpty(filteredUnitIds)) {
            setMeasures([])
            setVolumesLoaded(true)
            return
        }
        controllerRef.current.abort()
        initController()
        const { signal } = controllerRef.current
        setVolumesLoaded(false)
        ProductionUnitAction.promiseProductionUnitsVolumes({
            ids: filteredUnitIds,
            occurence: occurence.value,
            nbOccurence,
        }, signal)
            .then((json = []) => {
                setMeasures(json)
                setVolumesLoaded(true)
            })
            .catch(err => {
                if (signal.aborted) {
                    return
                }
                dispatch(LogAction.logError(`${i18n.fetchError + i18n.volumes} : ${err}`))
                dispatch(ToastrAction.error(i18n.fetchError + i18n.volumes))
                setVolumesLoaded(true)
            })
    }, [filteredUnitIds, occurence, nbOccurence, indicatorsLoaded])

    const columns = useMemo(
        () => range(nbOccurence).map(index => formatDate(moment().subtract(index, occurence.value))),
        [formatDate, nbOccurence, occurence.value],
    )

    // Une même station peut être liée à l'unité par plusieurs catégories de lien : une seule ligne.
    const stationsByUnit = useMemo(() => {
        const linkedStations = uniqBy(
            productionUnitsAssociations.filter(as => IN_SITE_TYPES.includes(as.stationLinkedType)
                || (as.stationLinkedType === STATION_TYPE_CONSTANT.installation && parseInt(as.linkType) >= PRODUCTION_UNIT_LINK_TYPES.DISTRIBUTION)),
            as => `${as.id}_${as.stationLinkedType}_${as.stationLinkedId}`,
        )
        return groupBy(linkedStations, 'id')
    }, [productionUnitsAssociations])

    const measuresByUnit = useMemo(() => groupBy(measures, 'productionUnitId'), [measures])

    const displayedUnits = useMemo(
        () => onlyWithData ? filteredUnits.filter(({ id }) => (measuresByUnit[id] || []).some(({ value }) => !isNil(value))) : filteredUnits,
        [filteredUnits, measuresByUnit, onlyWithData],
    )

    const selectValues = useMemo(() => {
        const labelKey = toLower(tmpOccurence.value)
        return range(tmpOccurence.nbValue).map(index => ({ label: `${index + 1} ${getI18nTitleDataLength(geti18n(labelKey), geti18n(`${labelKey}s`), index + 1)}`, id: index + 1 }))
    }, [tmpOccurence])

    const onToggleUnit = useCallback((unitId, isExpanded) => {
        setExpandedUnits(prev => ({ ...prev, [unitId]: isExpanded }))
    }, [setExpandedUnits])

    const getUnitStations = useCallback(unitId => {
        const unitStations = stationsByUnit[unitId] || []
        return {
            stationsIn: unitStations.filter(s => IN_SITE_TYPES.includes(s.stationLinkedType)).map(toVolumeStation),
            stationsOut: unitStations.filter(s => !IN_SITE_TYPES.includes(s.stationLinkedType)).map(toVolumeStation),
        }
    }, [stationsByUnit])

    // Tous les tableaux affichés dans un seul fichier, séparés par leur titre d'unité.
    useActions(() => ({
        export: () => {
            const tables = displayedUnits.map(productionUnit => toVolumesExportRows(
                getVolumesRows({
                    ...getUnitStations(productionUnit.id),
                    measures: measuresByUnit[productionUnit.id] || [],
                    columns,
                    formatDate,
                }),
                columns,
                getProductionUnitLabel(productionUnit),
            ))
            return {
                data: tables.length ? getManyTableData(tables, 'xlsx') : [],
                exportType: 'xlsx',
                titleFile: i18n.volumesManagement,
                doNotFormat: true,
            }
        },
    }), [columns, displayedUnits, formatDate, getUnitStations, measuresByUnit])

    if (!indicatorsLoaded) {
        return (
            <div style={{ marginTop: '10px', marginRight: '10px' }}>
                <ProgressCard progress={indicatorsProgress} withMessage />
            </div>
        )
    }

    return (
        <div style={{ marginTop: '10px', marginRight: '10px' }}>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <ProductionUnitFilterPanel key={resetKey} {...filterPanelProps} onSearch={onSearch} onReset={onReset}>
                        <Grid container spacing={2} alignItems='center'>
                            <Grid size={3}>
                                <ButtonGroup>
                                    {
                                        BUTTONS_OCCURENCE.map(o => (
                                            <ButtonMUI
                                                key={o.value}
                                                variant={tmpOccurence.value === o.value ? 'contained' : 'outlined'}
                                                onClick={() => {
                                                    setTmpOccurence(OCCURENCE_TYPE[o.value])
                                                    setTmpNbOccurence(OCCURENCE_TYPE[o.value].defaultValue)
                                                }}
                                                style={{ border: 'solid rgba(53, 96, 159, 1)', borderWidth: o.borderWidth, fontWeight: 600 }}
                                            >{i18n[o.i18nKey]}</ButtonMUI>
                                        ))
                                    }
                                </ButtonGroup>
                            </Grid>
                            <Grid size={3}>
                                <Select
                                    value={tmpNbOccurence}
                                    label={i18n.occurency}
                                    options={selectValues}
                                    noSort
                                    onChange={setTmpNbOccurence}
                                />
                            </Grid>
                            <Grid size={6}>
                                <SimpleCheckbox
                                    label={i18n.onlyUnitsWithData}
                                    checked={tmpOnlyWithData}
                                    onToggle={() => setTmpOnlyWithData(prev => !prev)}
                                />
                            </Grid>
                        </Grid>
                    </ProductionUnitFilterPanel>
                </Grid>
                <Grid size={12}>
                    {!volumesLoaded && <ProgressCard indeterminate />}
                    {volumesLoaded && displayedUnits.map(productionUnit => (
                        <ProductionUnitVolumesTable
                            key={productionUnit.id}
                            productionUnit={productionUnit}
                            {...getUnitStations(productionUnit.id)}
                            measures={measuresByUnit[productionUnit.id] || []}
                            columns={columns}
                            formatDate={formatDate}
                            expanded={!!expandedUnits[productionUnit.id]}
                            onToggle={onToggleUnit}
                            occurenceFilter={occurenceFilter}
                        />
                    ))}
                </Grid>
            </Grid>
        </div>
    )
}

const ProductionUnitsVolumesApp = () => {
    const dispatch = useDispatch()

    useEffect(() => {
        if (!componentHasHabilitations(H_PRODUCTION_VOLUMES)) {
            dispatch(push('/unauthorized'))
        }
    }, [dispatch])

    const { dataLoaded, progress } = useProductionUnitsData()

    useTitle(() => [{
        title: i18n[stationType],
        href: stationType,
    }, {
        title: i18n.volumesManagement,
        href: `${stationType}/volumes`,
    }], [])

    if (!dataLoaded) {
        return (
            <div style={{ marginTop: '10px', marginRight: '10px' }}>
                <ProgressCard progress={progress} withMessage />
            </div>
        )
    }

    return <ProductionUnitsVolumesPanel />
}

export default ProductionUnitsVolumesApp
